import { memo, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Alert,
  FormControlLabel,
  Switch,
  Divider,
  Box,
  Chip,
} from "@mui/material";

const Meli = ({
  ecommerce,
  warehouses,
  onSave,
  loading,
  isEdit,
  onConnect,
}) => {
  const [formData, setFormData] = useState(ecommerce);
  const [localError, setLocalError] = useState("");
  const [connecting, setConnecting] = useState(false);

  const handleWarehouseChange = (e) => {
    setFormData({ ...formData, warehouse: e.target.value });
  };

  const handleToggle = (key) => {
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        options: {
          ...prev.configuration.options,
          [key]: !prev.configuration.options[key],
        },
      },
    }));
  };

  const handleConnectClick = async () => {
    setLocalError("");
    setConnecting(true);

    try {
      const { success, redirectUri, error } = await onConnect(formData);

      if (success && redirectUri) {
        window.location.replace(redirectUri);
      } else {
        setLocalError(error || "No se pudo obtener la URL de autorización.");
      }
    } catch (error) {
      console.error(error);
      setLocalError("Error inesperado al intentar conectar.");
    } finally {
      setConnecting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const isConnected =
    !!formData.configuration.authentication.integration_user_id;

  return (
    <form>
      <Typography variant="h6" gutterBottom>
        Configuración Mercado Libre
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Vincula tu cuenta para sincronizar ventas y etiquetas. Configura qué
        tipos de envío quieres gestionar.
      </Alert>

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {localError}
        </Alert>
      )}

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Bodega por defecto</InputLabel>
        <Select
          value={formData.warehouse || ""}
          label="Bodega por defecto"
          onChange={handleWarehouseChange}
        >
          {warehouses.map((w) => (
            <MenuItem key={w.id} value={w.id}>
              {w.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ mb: 3, textAlign: "center" }}>
        {!isConnected ? (
          <Button
            variant="contained"
            color="warning"
            fullWidth
            size="large"
            onClick={handleConnectClick}
            disabled={loading || connecting}
          >
            {connecting || loading
              ? "Redirigiendo..."
              : "Vincular cuenta de Mercado Libre"}
          </Button>
        ) : (
          <Chip
            label={`Conectado (User ID: ${formData.configuration.authentication.integration_user_id})`}
            color="success"
            variant="outlined"
            sx={{ width: "100%", py: 3, fontSize: "1rem" }}
          />
        )}
      </Box>

      {isConnected && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold" }}>
            Configuración de Envíos
          </Typography>

          <Grid
            container
            spacing={2}
            sx={{ mb: 3, border: "1px solid #e0e0e0", borderRadius: 2, p: 2 }}
          >
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      formData.configuration.options.fulfillment || false
                    }
                    onChange={() => handleToggle("fulfillment")}
                  />
                }
                label="Fulfillment (Full)"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      formData.configuration.options.self_service || false
                    }
                    onChange={() => handleToggle("self_service")}
                  />
                }
                label="Flex"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      formData.configuration.options.xd_drop_off || false
                    }
                    onChange={() => handleToggle("xd_drop_off")}
                  />
                }
                label="XD Drop OFF"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      formData.configuration.options.cross_docking || false
                    }
                    onChange={() => handleToggle("cross_docking")}
                  />
                }
                label="Cross Docking"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.configuration.options.turbo || false}
                    onChange={() => handleToggle("turbo")}
                  />
                }
                label="Turbo"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.configuration.options.custom || false}
                    onChange={() => handleToggle("custom")}
                  />
                }
                label="Personalizado"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      formData.configuration.options.automatic_print_label ||
                      false
                    }
                    onChange={() => handleToggle("automatic_print_label")}
                    color="primary"
                  />
                }
                label={
                  <Typography fontWeight="bold">
                    Imprimir etiquetas automáticamente desde MELI
                  </Typography>
                }
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold" }}>
            Tokens de Acceso
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Access Token"
                value={formData.configuration.authentication.token || ""}
                disabled
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Refresh Token"
                value={
                  formData.configuration.authentication.refresh_token || ""
                }
                disabled
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="User ID Integración"
                value={
                  formData.configuration.authentication.integration_user_id ||
                  ""
                }
                disabled
                size="small"
              />
            </Grid>
          </Grid>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            size="large"
            onClick={handleSubmit}
          >
            Actualizar Configuración
          </Button>
        </>
      )}
    </form>
  );
};

export default memo(Meli);
